import { useEffect, useMemo, useState } from "react";
import { Modal, Tabs, Button, Typography, Tooltip, Image, message } from "antd";
import { TbUpload, TbChartBar, TbX, TbTrash, TbFile, TbPhoto, TbAlertCircle } from "react-icons/tb";
import { getIcons } from "./email-message";
import { formatBytes } from "utils/functions";
import { EmptyAttachmentsIllustration } from "assets/empty/attachments";

export type ContentType =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "text"
  | "archive"
  | "spreadsheet"
  | "document"
  | "presentation"
  | "binary"
  | "unknown";

export const getContentTypeFromFile = (file: File): ContentType => {
  const mime = file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();

  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (mime.startsWith("text/") || ["txt", "md", "csv"].includes(ext ?? "")) return "text";
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext ?? "")) return "archive";

  if (
    mime === "application/vnd.ms-excel" ||
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    ["xls", "xlsx"].includes(ext ?? "")
  )
    return "spreadsheet";

  if (
    mime === "application/msword" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    ["doc", "docx"].includes(ext ?? "")
  )
    return "document";

  if (
    mime === "application/vnd.ms-powerpoint" ||
    mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    ["ppt", "pptx"].includes(ext ?? "")
  )
    return "presentation";

  if (!mime && ext) return "binary";
  return "unknown";
};

export const allowedMimeTypes = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "video/mp4",
  "video/mpeg",
  "video/quicktime",
  "video/x-msvideo",
  "video/webm",
  "video/ogg",
]);

export const allowedExtensions = new Set(["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx"]);

interface AttachmentGroup {
  label: string;
  items: { file: File; index: number }[];
}

const GROUP_ORDER: { label: string; types: ContentType[] }[] = [
  { label: "IMAGES", types: ["image"] },
  { label: "DOCUMENTS", types: ["pdf", "document", "spreadsheet", "presentation", "text"] },
  { label: "MEDIA", types: ["video", "audio"] },
  { label: "OTHER", types: ["archive", "binary", "unknown"] },
];

const MAX_TEXT_PREVIEW_BYTES = 50_000;

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

interface AttachmentsModalProps {
  open: boolean;
  onClose: () => void;
  attachedFiles: File[];
  onRemoveFile: (index: number) => void;
  onUploadClick: () => void;
  isFileLimitReached: boolean;
  onOpenChartsPicker: () => void;
  chartsDisabled: boolean;
  chartsError?: string;
  onInsertImageIntoBody: (dataUrl: string, index: number) => void;
}

const AttachmentsModal: React.FC<AttachmentsModalProps> = ({
  open,
  onClose,
  attachedFiles,
  onRemoveFile,
  onUploadClick,
  isFileLimitReached,
  onOpenChartsPicker,
  chartsDisabled,
  chartsError,
  onInsertImageIntoBody,
}) => {
  const [activeTab, setActiveTab] = useState<"upload" | "charts">("upload");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textPreview, setTextPreview] = useState<string | null>(null);
  const [textPreviewTruncated, setTextPreviewTruncated] = useState(false);
  const [isInserting, setIsInserting] = useState(false);

  const hasFiles = attachedFiles.length > 0;
  const selectedFile = attachedFiles[selectedIndex];
  const contentType: ContentType | undefined = selectedFile ? getContentTypeFromFile(selectedFile) : undefined;

  const groupedAttachments: AttachmentGroup[] = useMemo(() => {
    return GROUP_ORDER.map((group) => ({
      label: group.label,
      items: attachedFiles
        .map((file, index) => ({ file, index }))
        .filter(({ file }) => group.types.includes(getContentTypeFromFile(file))),
    })).filter((group) => group.items.length > 0);
  }, [attachedFiles]);

  useEffect(() => {
    if (open) {
      setSelectedIndex(0);
      setActiveTab("upload");
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex((prev) => (attachedFiles.length === 0 ? 0 : Math.min(prev, attachedFiles.length - 1)));
  }, [attachedFiles.length]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  useEffect(() => {
    setTextPreview(null);
    setTextPreviewTruncated(false);
    if (!selectedFile || contentType !== "text") return;

    const truncated = selectedFile.size > MAX_TEXT_PREVIEW_BYTES;
    const blobToRead = truncated ? selectedFile.slice(0, MAX_TEXT_PREVIEW_BYTES) : selectedFile;

    const reader = new FileReader();
    reader.onload = () => {
      setTextPreview(String(reader.result ?? ""));
      setTextPreviewTruncated(truncated);
    };
    reader.readAsText(blobToRead);
  }, [selectedFile, contentType]);

  const handleInsertIntoBody = async () => {
    if (!selectedFile || contentType !== "image") return;
    setIsInserting(true);
    try {
      const dataUrl = await readFileAsDataUrl(selectedFile);
      onInsertImageIntoBody(dataUrl, selectedIndex);
    } catch {
      message.error("Could not insert this image into the email body");
    } finally {
      setIsInserting(false);
    }
  };

  const actionButton =
    activeTab === "upload" ? (
      <Button type="primary" block icon={<TbUpload size={14} />} disabled={isFileLimitReached} onClick={onUploadClick}>
        Upload Files
      </Button>
    ) : (
      <Button
        type="primary"
        block
        icon={<TbChartBar size={14} />}
        disabled={chartsDisabled}
        onClick={onOpenChartsPicker}
      >
        Browse Charts
      </Button>
    );

  const renderPreview = () => {
    if (!hasFiles) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center gap-3 px-6">
          <EmptyAttachmentsIllustration />
          <Typography.Text type="secondary">No attachments yet.</Typography.Text>
          <div className="w-[200px]">{actionButton}</div>
        </div>
      );
    }

    if (!selectedFile || !previewUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
          <TbFile size={28} />
          <Typography.Text type="secondary">No attachment selected</Typography.Text>
        </div>
      );
    }

    switch (contentType) {
      case "image":
        return (
          <Image
            src={previewUrl}
            preview={false}
            alt={selectedFile.name}
            className="max-h-full max-w-full object-contain"
          />
        );
      case "pdf":
        return <iframe src={previewUrl} title={selectedFile.name} className="w-full h-full border-0" />;
      case "video":
        return <video src={previewUrl} controls className="max-h-full max-w-full" />;
      case "audio":
        return <audio src={previewUrl} controls className="w-full" />;
      case "text":
        return (
          <div className="w-full h-full overflow-auto p-3 bg-white">
            <pre className="text-xs whitespace-pre-wrap break-words">{textPreview ?? "Loading preview..."}</pre>
            {textPreviewTruncated && (
              <Typography.Text type="secondary" className="!text-xs block mt-2">
                Preview truncated — showing first 50KB only.
              </Typography.Text>
            )}
          </div>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
            <TbFile size={28} />
            <Typography.Text type="secondary">Preview not supported for this file type</Typography.Text>
            <a href={previewUrl} download={selectedFile.name} className="text-xs text-purple-600 underline">
              Download to view
            </a>
          </div>
        );
    }
  };

  return (
    <Modal
      title={
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Attach to Email
          </Typography.Title>
          <Typography.Text type="secondary" className="!text-xs">
            Upload files or attach charts, and optionally insert images directly into the body.
          </Typography.Text>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <div className="pt-1">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as "upload" | "charts")}
          items={[
            {
              key: "upload",
              label: (
                <span className="flex items-center gap-1">
                  <TbUpload size={14} /> Local Files
                </span>
              ),
              children: null,
            },
            {
              key: "charts",
              label: (
                <span className="flex items-center gap-1">
                  <TbChartBar size={14} /> Charts
                  {chartsError && (
                    <Tooltip title={chartsError} className="text-red-400">
                      <TbAlertCircle />
                    </Tooltip>
                  )}
                </span>
              ),
              children: null,
            },
          ]}
        />

        <div className="space-y-1">
          {/* <Typography.Text type="secondary" className="!text-xs block">
            {activeTab === "upload"
              ? "Images, PDF, Word, and Excel files up to 25MB each."
              : "Attach APR charts generated for this application."}
          </Typography.Text>
          {isFileLimitReached && (
            <Typography.Text type="danger" className="!text-xs block">
              Max 5 files can be attached
            </Typography.Text>
          )} */}
        </div>

        <div className="flex gap-4">
          {hasFiles && (
            <div className="w-[260px] shrink-0 border border-gray-200 rounded-lg flex flex-col max-h-[380px] overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                {groupedAttachments.map((group) => (
                  <div key={group.label}>
                    <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50">
                      <Typography.Text className="!text-[11px] !text-gray-400 tracking-wide font-medium">
                        {group.label}
                      </Typography.Text>
                      <Typography.Text className="!text-[11px] !text-gray-400">{group.items.length}</Typography.Text>
                    </div>
                    {group.items.map(({ file, index }) => {
                      const isSelected = index === selectedIndex;
                      return (
                        <div
                          key={`${file.name}-${index}`}
                          onClick={() => setSelectedIndex(index)}
                          className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                            isSelected ? "bg-purple-50 border-l-2 border-l-purple-500" : "hover:bg-gray-50"
                          }`}
                        >
                          <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0">
                            {getIcons(getContentTypeFromFile(file))}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-sm text-gray-700">{file.name}</div>
                            <div className="text-[11px] text-gray-400 font-mono">{formatBytes(file.size)}</div>
                          </div>
                          <Button
                            type="text"
                            size="small"
                            icon={<TbX size={14} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveFile(index);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="p-2 border-t border-gray-100 shrink-0">{actionButton}</div>
            </div>
          )}

          <div className="flex-1 flex flex-col min-h-[380px] max-h-[380px] overflow-hidden">
            {hasFiles && selectedFile && (
              <div className="flex items-center justify-between mb-3 pt-2 pb-1 gap-2 border-b border-gray-100 shrink-0">
                <Typography.Text strong className="truncate">
                  {selectedFile.name}
                </Typography.Text>
                <div className="flex gap-2 shrink-0">
                  <Tooltip title={contentType === "image" ? "" : "Only images can be inserted into the email body"}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="geekblue"
                      icon={<TbPhoto size={14} />}
                      disabled={contentType !== "image"}
                      loading={isInserting}
                      onClick={handleInsertIntoBody}
                    >
                      Insert into body
                    </Button>
                  </Tooltip>
                  <Button size="small" danger icon={<TbTrash size={14} />} onClick={() => onRemoveFile(selectedIndex)}>
                    Remove
                  </Button>
                </div>
              </div>
            )}
            <div className="flex-1 overflow-auto flex items-center justify-center border border-solid border-gray-200 rounded">
              {renderPreview()}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AttachmentsModal;
